import { useState, useEffect } from 'react'
import { cohortsAPI } from '../../services/api'
import CohortStatusBadge from './CohortStatusBadge'

/**
 * Vista de monitoreo de cohortes activas (collecting / locked / running).
 * Muestra una tabla con: campaña, fecha, estado, paso actual, inscritos, enviados,
 * pendientes, próximo envío, creada por.
 *
 * Auto-refresh cada 30s. Permite expandir cada fila para ver inscritos.
 */
export default function ActiveCohortsView({ isAdmin }) {
  const [cohorts, setCohorts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [onlyMine, setOnlyMine] = useState(!isAdmin)
  const [expandedId, setExpandedId] = useState(null)
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const load = async () => {
    try {
      setError(null)
      const r = await cohortsAPI.getActive({ onlyMine })
      setCohorts(r.cohorts || [])
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setLoading(true)
    load()
    const id = setInterval(load, 30000)
    return () => clearInterval(id)
  }, [onlyMine])

  const toggleExpand = async (cohortId) => {
    if (expandedId === cohortId) {
      setExpandedId(null)
      setDetail(null)
      return
    }
    setExpandedId(cohortId)
    setDetailLoading(true)
    try {
      const [d, failuresR] = await Promise.all([
        cohortsAPI.get(cohortId),
        cohortsAPI.getFailures(cohortId)
      ])
      setDetail({ ...d, failures: failuresR.failures || [] })
    } catch (e) {
      console.error('[ActiveCohortsView] detail/failures error:', e)
    } finally {
      setDetailLoading(false)
    }
  }

  const formatDateTime = (iso) => {
    if (!iso) return '—'
    const d = new Date(iso)
    return d.toLocaleString('es-CL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  }

  if (loading) return <div className="p-6 text-gray-500">Cargando cohortes…</div>
  if (error) return <div className="p-6 text-red-600">Error: {error}</div>

  return (
    <div className="flex-1 p-6 overflow-y-auto bg-gray-50">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Cohortes activas</h3>
          <p className="text-xs text-gray-500">{cohorts.length} cohorte(s) en curso · auto-refresh c/30s</p>
        </div>
        {isAdmin && (
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={!onlyMine} onChange={() => setOnlyMine(!onlyMine)} className="w-4 h-4" />
            Ver todas
          </label>
        )}
      </div>

      {cohorts.length === 0 ? (
        <div className="text-center py-12 text-gray-400 bg-white rounded-2xl">
          No hay cohortes activas en este momento.
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Campaña</th>
                <th className="px-3 py-2 text-left font-medium">Inicio</th>
                <th className="px-3 py-2 text-left font-medium">Estado</th>
                <th className="px-3 py-2 text-center font-medium">Paso</th>
                <th className="px-3 py-2 text-center font-medium" title="Inscritos totales">👥</th>
                <th className="px-3 py-2 text-center font-medium" title="Mensajes enviados">📤</th>
                <th className="px-3 py-2 text-center font-medium" title="Pendientes (active)">⏳</th>
                <th className="px-3 py-2 text-left font-medium">Próximo envío</th>
                <th className="px-3 py-2 text-left font-medium">Creada por</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {cohorts.map(co => (
                <>
                  <tr key={co.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-800 truncate max-w-[180px]">{co.campaign_name}</td>
                    <td className="px-3 py-2 text-gray-600">{formatDateTime(co.start_at)}</td>
                    <td className="px-3 py-2"><CohortStatusBadge status={co.status} /></td>
                    <td className="px-3 py-2 text-center font-mono">{co.max_step ?? '—'}</td>
                    <td className="px-3 py-2 text-center">{co.enrollment_count}</td>
                    <td className="px-3 py-2 text-center text-emerald-700">{co.sent_count}</td>
                    <td className="px-3 py-2 text-center text-amber-700">{co.pending_count}</td>
                    <td className="px-3 py-2 text-gray-600 text-xs">{formatDateTime(co.next_send_at)}</td>
                    <td className="px-3 py-2 text-gray-500 text-xs">{co.created_by}</td>
                    <td className="px-3 py-2">
                      <button onClick={() => toggleExpand(co.id)} className="text-purple-600 hover:text-purple-800 text-xs font-medium">
                        {expandedId === co.id ? '▲' : '▼'}
                      </button>
                    </td>
                  </tr>
                  {expandedId === co.id && (
                    <tr key={`${co.id}-detail`}>
                      <td colSpan={10} className="px-3 py-3 bg-gray-50">
                        {detailLoading ? (
                          <div className="text-gray-500 text-xs">Cargando inscritos…</div>
                        ) : detail ? (
                          <div>
                            <div className="text-xs font-medium text-gray-700 mb-2">Inscritos ({(detail.enrollments || []).length}):</div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                              {(detail.enrollments || []).map(e => (
                                <div key={e.id} className="bg-white rounded-lg p-2 border border-gray-200">
                                  <div className="font-medium text-gray-800 truncate">{e.contact_name || e.contact_phone}</div>
                                  <div className="text-gray-500 flex justify-between">
                                    <span>{e.contact_phone}</span>
                                    <span className="text-[10px] uppercase">paso {e.current_step} · {e.status}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {detail?.failures && detail.failures.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-red-100">
                                <div className="text-xs font-medium text-red-700 mb-2">
                                  ⚠ Con problemas ({detail.failures.length}):
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                  {detail.failures.map(f => (
                                    <div key={f.enrollment_id} className="bg-red-50 rounded-lg p-2 border border-red-200">
                                      <div className="font-medium text-gray-800 truncate">{f.contact_name || f.contact_phone}</div>
                                      <div className="text-gray-600 flex justify-between mt-1">
                                        <span className="text-[10px]">{f.contact_phone} · paso {f.current_step} · intentos: {f.retry_count}</span>
                                        <span className="text-red-700 uppercase text-[10px] font-bold">
                                          {f.status === 'exited' ? (f.exit_reason || 'salió') : 'reintentando'}
                                        </span>
                                      </div>
                                      {f.failed_steps?.length > 0 && (
                                        <div className="text-[10px] text-gray-500 mt-1">
                                          Pasos fallidos: {f.failed_steps.map(s => 'P' + s.step_number).join(', ')}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
